import {
    LoadingOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import {FormInstance} from 'antd/es/form';
import cn from 'classnames';
import React, {
    forwardRef,
    useImperativeHandle, useRef, useState, ReactElement,
} from 'react';

import {ModalOpenerComponent, IModalOpenerComponentRef} from 'components/modal-opener-component';
import {ISendSignedFileArgs} from 'features/sign-with-crypto-pro/api/types';
import {EFileEncoding} from 'features/sign-with-crypto-pro/model/constants';
import {EFileExtensions} from 'shared/constants/file-extensions';

import {useCryptoProSigning} from '../../hooks';
import {getDocumentsIdsToSign} from '../../lib';
import {
    TCertificateExtended, TCryptoProSignatureType, ICryptoProSignedDoc, TSignWithCryptoProModalDisabledFields,
} from '../../model/types';
import {CryptoProSignatureModalContent} from '../signature-modal-content/signature-modal-content';
import s from './sign-with-crypto-pro-modal.module.less';

interface ISignWithCryptoProModalProps {
    documentIds?: string[]; // todo: нужна типизация
    attachmentIds?: string[];
    onFinish?: () => void;
    onStart?: () => void;
    handleSignedFileUpload?: (args: ISendSignedFileArgs) => void;
    entityName: string;
    className?: cn.Argument;
    triggerButton?: ReactElement;
    disabledFields?: TSignWithCryptoProModalDisabledFields;
    defaultFileExtension?: EFileExtensions;
}

export const SignWithCryptoProModal = forwardRef<any, ISignWithCryptoProModalProps>((
    {
        documentIds,
        attachmentIds,
        onFinish,
        onStart,
        handleSignedFileUpload,
        entityName,
        className,
        triggerButton,
        disabledFields,
        defaultFileExtension,
    }: ISignWithCryptoProModalProps,
    ref,
) => {
    const [selectedCertificate, setSelectedCertificate] = useState<TCertificateExtended>();
    const [selectedSignatureType, setSelectedSignatureType] = useState<TCryptoProSignatureType>('detached');
    const [selectedFileExtension, setSelectedFileExtension] = useState<string | undefined>();
    const [selectedFileEncoding, setSelectedFileEncoding] = useState<EFileEncoding | undefined>();
    const [isWithPluginInitializationError, setIsWithPluginInitializationError] = useState(false);
    const [isSignButtonTemporarilyInactive, setIsSignButtonTemporarilyInactive] = useState(false);
    const [signedDocs, setSignedDocs] = useState<ICryptoProSignedDoc[]>([]);
    const [isWithResign, setIsWithResign] = useState(true);

    const formRef = useRef<FormInstance>(null);
    const modalRef = useRef<IModalOpenerComponentRef | null>(null);

    const documentIdsToSign = documentIds?.length
        ? getDocumentsIdsToSign(isWithResign, documentIds, signedDocs) : undefined;
    const attachmentIdsToSign = attachmentIds?.length
        ? getDocumentsIdsToSign(isWithResign, attachmentIds, signedDocs) : undefined;

    const {
        handleFinish,
    } = useCryptoProSigning({
        entityName,
        onFinish,
        onStart,
        handleSignedFileUpload,
        isWithResign,
        selectedCertificate,
        selectedSignatureType,
        selectedFileExtension,
        selectedFileEncoding,
        modalRef,
        documentIdsToSign,
        attachmentIdsToSign,
    });

    const resetState = () => {
        setSelectedCertificate(undefined);
        setIsWithPluginInitializationError(false);
        setIsSignButtonTemporarilyInactive(false);
        setSelectedSignatureType('detached');
        setSelectedFileExtension(undefined);
        setSignedDocs([]);
        setIsWithResign(true);
    };

    useImperativeHandle(ref, () => modalRef.current);

    return (
        <>
            <ModalOpenerComponent
                ref={modalRef}
                componentWrapperClassNames={className}
                modalProps={{
                    centered: true,
                    forceRender: false,
                    destroyOnClose: true,
                    title: 'Настройка подписи',
                }}
                hideControls={{
                    save: isWithPluginInitializationError,
                }}
                disabledControls={{
                    save: !selectedCertificate || isSignButtonTemporarilyInactive
                    || (!documentIdsToSign?.length && !attachmentIdsToSign?.length),
                }}
                controlLabels={{
                    save: isSignButtonTemporarilyInactive ? (
                        <div>
                            <LoadingOutlined className={s['sign-loading-icon']} />
                            Подписать
                        </div>
                    ) : 'Подписать',
                    cancel: isWithPluginInitializationError ? 'Закрыть' : undefined,
                }}
                handleSave={() => {
                    setIsSignButtonTemporarilyInactive(true);
                    setTimeout(() => {
                        setIsSignButtonTemporarilyInactive(false);
                    }, 1000);
                    formRef.current?.submit();
                }}
                component={triggerButton || (
                    <div className={s['sign-component']}>
                        <SafetyCertificateOutlined />
                        <span>Подписать</span>
                    </div>
                )}
                afterModalClose={resetState}
            >
                <CryptoProSignatureModalContent
                    ref={formRef}
                    selectedCertificate={selectedCertificate}
                    setSelectedCertificate={setSelectedCertificate}
                    selectedSignatureType={selectedSignatureType}
                    setSelectedSignatureType={setSelectedSignatureType}
                    selectedFileExtension={selectedFileExtension}
                    setSelectedFileExtension={setSelectedFileExtension}
                    selectedFileEncoding={selectedFileEncoding}
                    setSelectedFileEncoding={setSelectedFileEncoding}
                    isWithPluginInitializationError={isWithPluginInitializationError}
                    setIsWithPluginInitializationError={setIsWithPluginInitializationError}
                    handleFinish={handleFinish}
                    documentIds={documentIds}
                    setSignedDocs={setSignedDocs}
                    signedDocs={signedDocs}
                    isWithResign={isWithResign}
                    setIsWithResign={setIsWithResign}
                    disabledFields={disabledFields}
                    defaultFileExtension={defaultFileExtension}
                />
            </ModalOpenerComponent>
        </>
    );
});
